import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CheckCircle2, Clock, Send, Sparkles } from 'lucide-react';
import { apiPost } from '../../lib/api.js';
import { fmtFromNow } from '../../lib/format.js';

/**
 * Source B: ask a team manager / peer to approve cloning their setup (24h expiry).
 * @param {{managers: Array<object>, isLoadingManagers: boolean, latestRequest: object|null,
 *   isPending: boolean, isApproved: boolean}} props
 * @returns {JSX.Element}
 */
export default function ManagerSyncPanel({ managers, isLoadingManagers, latestRequest, isPending, isApproved }) {
  const qc = useQueryClient();
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [note, setNote] = useState('');

  // Pre-select the direct recruiter when there is one.
  useEffect(() => {
    if (!selectedManagerId && managers.length > 0) {
      setSelectedManagerId(String((managers.find((m) => m.isDirectManager) || managers[0])._id));
    }
  }, [managers, selectedManagerId]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['sync-status'] });
    qc.invalidateQueries({ queryKey: ['sync-history'] });
  };

  const sendRequest = useMutation({
    mutationFn: () => apiPost('/sync/request', { targetManagerId: selectedManagerId, syncedItems: ['sms_drips', 'email_templates', 'ai_bots', 'tags'], note }),
    onSuccess: () => { toast.success('Sync request sent to your manager!'); setNote(''); refresh(); },
    onError: (err) => toast.error(err?.response?.data?.error || err.message || 'Failed to submit sync request'),
  });

  const cancel = useMutation({
    mutationFn: (id) => apiPost(`/sync/${id}/cancel`),
    onSuccess: () => { toast.success('Sync request cancelled'); refresh(); },
    onError: (err) => toast.error(err?.response?.data?.error || err.message || 'Cancellation failed'),
  });

  const targetManager = managers.find((m) => String(m._id) === String(selectedManagerId));

  return (
    <div className="space-y-4" data-testid="manager-sync-panel">
      {isPending && (
        <div className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/10 flex items-start justify-between gap-3 animate-pulse">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-amber-200 text-sm">Sync Request Pending Manager Approval</div>
              <div className="text-amber-200/70 text-xs mt-0.5">
                Sent to <span className="font-semibold text-white">{latestRequest?.targetManagerId?.name || latestRequest?.toManager?.name || 'your manager'}</span> {fmtFromNow(latestRequest?.requestedAt || latestRequest?.createdAt)}. Requests expire after 24 hours. Once approved, all operational templates will clone into your CRM automatically.
              </div>
            </div>
          </div>
          <button onClick={() => cancel.mutate(latestRequest?._id)} disabled={cancel.isPending} className="text-xs text-amber-300/80 hover:text-amber-100 underline shrink-0 cursor-pointer">
            Cancel Request
          </button>
        </div>
      )}

      <div className="space-y-1">
        <h2 className="font-display text-xl font-bold text-white flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-civix-purpleSoft" /> Sync with Your Team Manager
        </h2>
        <p className="text-white/60 text-xs">Select your direct recruiter or team lead to inherit their production-ready templates.</p>
      </div>

      <div className="space-y-2">
        <label className="block text-xs uppercase tracking-wider text-white/50 font-semibold">Select Team Manager</label>
        <select
          value={selectedManagerId}
          onChange={(e) => setSelectedManagerId(e.target.value)}
          disabled={isPending || isLoadingManagers}
          className="w-full bg-[#130b20] border border-white/15 rounded-xl px-4 py-3 text-sm text-white font-medium focus:border-civix-cyan focus:outline-hidden disabled:opacity-50 transition cursor-pointer"
        >
          {managers.map((m) => (
            <option key={String(m._id)} value={String(m._id)}>
              {m.name || m.email} · {m.role} {m.isDirectManager ? '★ (Your Direct Manager)' : ''}
            </option>
          ))}
        </select>
        {targetManager?.isDirectManager && (
          <div className="text-xs text-civix-cyan flex items-center gap-1.5 pt-0.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> Pre-selected: This is your direct manager who added you to CIVIX.
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <label className="block text-xs uppercase tracking-wider text-white/50 font-semibold">Note to Manager (Optional)</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={isPending}
          placeholder="E.g., Finishing onboarding Day 1 — please sync your latest Final Expense and Veteran bots!"
          className="w-full bg-[#130b20] border border-white/15 rounded-xl px-4 py-2.5 text-sm text-white focus:border-civix-cyan focus:outline-hidden resize-none disabled:opacity-50"
          rows={2}
          maxLength={500}
        />
      </div>

      <button
        onClick={() => sendRequest.mutate()}
        disabled={!selectedManagerId || isPending || sendRequest.isPending}
        className="w-full py-3.5 px-6 rounded-xl font-bold text-sm bg-civix-gradient hover:opacity-95 text-white shadow-lg shadow-civix-purple/30 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center justify-center gap-2 cursor-pointer"
      >
        <Send className="w-4 h-4" />
        {sendRequest.isPending ? 'Sending Sync Request…' : isPending ? 'Sync Request Pending Approval' : isApproved ? 'Re-Sync Manager Templates' : 'Send Sync Request to Manager'}
      </button>
    </div>
  );
}
